/**
 * Typed API client. Wraps fetch with:
 *   • automatic access-token injection (from the auth store, in-memory only)
 *   • transparent refresh-on-401 with single-flight (concurrent 401s share one
 *     refresh)
 *   • centralized error normalisation to ApiError
 *
 * The access token lives ONLY in memory (the auth store). The refresh token is
 * an httpOnly cookie the browser manages for us — JS never reads it.
 */

import type { ApiError } from "@/types";

const JSON_HEADERS = { Accept: "application/json" } as const;

export class ApiException extends Error {
  code: string;
  status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function parseError(res: Response): Promise<ApiException> {
  try {
    const body = (await res.clone().json()) as ApiError;
    return new ApiException(res.status, body.error.code, body.error.message);
  } catch {
    return new ApiException(res.status, "HTTP_ERROR", res.statusText || `HTTP ${res.status}`);
  }
}

// Injected by the auth store to avoid a circular import. The store registers
// its token getter + refresh trigger here on module load.
interface TokenProvider {
  getAccessToken: () => string | null;
  refresh: () => Promise<string | null>; // returns new access token or null
  clear: () => void;
}
let tokenProvider: TokenProvider | null = null;
export function registerTokenProvider(p: TokenProvider) {
  tokenProvider = p;
}

let refreshInFlight: Promise<string | null> | null = null;
function refreshSingleFlight(): Promise<string | null> {
  if (!tokenProvider) return Promise.resolve(null);
  if (!refreshInFlight) {
    refreshInFlight = tokenProvider.refresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Skip auth header (for /login, /refresh). */
  noAuth?: boolean;
  /** Don't attempt refresh on 401 (used by refresh itself). */
  noRefresh?: boolean;
  signal?: AbortSignal;
}

interface RawRequestOptions {
  method?: string;
  body?: BodyInit;
  headers?: Record<string, string>;
  noAuth?: boolean;
  noRefresh?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, noAuth, noRefresh, signal } = opts;
  const headers: Record<string, string> = { ...JSON_HEADERS };
  if (!noAuth && tokenProvider?.getAccessToken()) {
    headers.Authorization = `Bearer ${tokenProvider.getAccessToken()}`;
  }
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const init: RequestInit = {
    method,
    headers,
    signal,
  };
  if (body !== undefined) init.body = JSON.stringify(body);

  const res = await fetch(path, init);

  // Transparent refresh on 401 (once).
  if (res.status === 401 && !noRefresh && tokenProvider) {
    const newToken = await refreshSingleFlight();
    if (newToken) {
      headers.Authorization = `Bearer ${newToken}`;
      const retryRes = await fetch(path, { ...init, headers });
      if (retryRes.ok) return (await retryRes.json()) as T;
      throw await parseError(retryRes);
    }
    // Refresh failed → clear and let the 401 propagate.
    tokenProvider.clear();
  }

  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function requestRaw<T>(path: string, opts: RawRequestOptions = {}): Promise<T> {
  const { method = "POST", body, headers: extraHeaders, noAuth, noRefresh, signal } = opts;
  const headers: Record<string, string> = { Accept: "application/json", ...extraHeaders };
  if (!noAuth && tokenProvider?.getAccessToken()) {
    headers.Authorization = `Bearer ${tokenProvider.getAccessToken()}`;
  }

  const init: RequestInit = {
    method,
    headers,
    body,
    signal,
  };

  const res = await fetch(path, init);
  if (res.status === 401 && !noRefresh && tokenProvider) {
    const newToken = await refreshSingleFlight();
    if (newToken) {
      headers.Authorization = `Bearer ${newToken}`;
      const retryRes = await fetch(path, { ...init, headers });
      if (retryRes.ok) return retryRes.status === 204 ? undefined as T : (await retryRes.json()) as T;
      throw await parseError(retryRes);
    }
    tokenProvider.clear();
  }

  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown, opts: RequestOptions = {}) =>
    request<T>(path, { method: "POST", body, ...opts }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body }),
  del: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "DELETE", body }),
  upload: <T>(path: string, body: BodyInit, headers: Record<string, string>) =>
    requestRaw<T>(path, { method: "POST", body, headers }),
};
